/**
 * shared/gopWorkspace.js — gộp hai bản Bản chuẩn bị: bản trên thiết bị và bản
 * trên tài khoản.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * VÌ SAO KHÔNG "BÊN NÀO MỚI HƠN THÌ THẮNG CẢ CỤC":
 *
 * Người dùng nhập runway trên máy tính ở văn phòng, tối về mở điện thoại sửa
 * checklist. Nếu lấy nguyên cục mới hơn, bản nháp runway cả buổi chiều biến
 * mất và họ không có cách nào biết vì sao. Mất dữ liệu kiểu này chỉ cần xảy ra
 * một lần là người dùng không bao giờ tin tính năng đồng bộ nữa.
 *
 * Nên gộp ở mức từng phần:
 *  - bản nháp: so theo từng công cụ, công cụ nào sửa sau thì giữ bản đó
 *  - kịch bản và nguồn vốn đã lưu: gộp cả hai bên, trùng id thì giữ bản mới
 *  - checklist và hồ sơ: so theo `capNhat` của cả bản, vì đây là dữ liệu nhỏ
 *    và sửa cùng lúc; riêng hồ sơ thì ô nào bên mới để trống vẫn lấy bên cũ
 *
 * Hàm này thuần: không đụng localStorage, không đụng mạng, nên kiểm thử được.
 * ─────────────────────────────────────────────────────────────────────────
 */

const TRONG = {
  version: 1, drafts: {}, saved: [], tasks: {}, profile: {}, scenarios: [],
  xoa: {}, profileMoc: {},
};

/** Giữ dấu xoá bao lâu. Quá hạn này thì mọi thiết bị đã đồng bộ xong từ lâu,
 *  giữ thêm chỉ làm phình dữ liệu. */
const HAN_DAU_XOA_NGAY = 180;

const luc = (x) => {
  const t = Date.parse(x);
  return Number.isFinite(t) ? t : 0;
};

/** Bản rỗng hoặc hỏng coi như không có gì, để bên kia thắng mà không mất dữ liệu. */
const chuan = (x) => (x && typeof x === 'object' && !Array.isArray(x)
  ? {
    ...TRONG,
    ...x,
    drafts: x.drafts && typeof x.drafts === 'object' ? x.drafts : {},
    saved: Array.isArray(x.saved) ? x.saved : [],
    tasks: x.tasks && typeof x.tasks === 'object' ? x.tasks : {},
    profile: x.profile && typeof x.profile === 'object' ? x.profile : {},
    scenarios: Array.isArray(x.scenarios) ? x.scenarios : [],
    xoa: x.xoa && typeof x.xoa === 'object' ? x.xoa : {},
    profileMoc: x.profileMoc && typeof x.profileMoc === 'object' ? x.profileMoc : {},
  }
  : { ...TRONG });

/** Có nội dung thật hay chỉ là khung rỗng. */
export function coNoiDung(x) {
  const a = chuan(x);
  return Object.keys(a.drafts).length > 0
    || a.saved.length > 0
    || a.scenarios.length > 0
    || Object.values(a.tasks).some(Boolean)
    || Object.values(a.profile).some((v) => v !== '' && v != null);
}

/**
 * @param {object} a bản thứ nhất (thường là bản trên thiết bị)
 * @param {object} b bản thứ hai (thường là bản trên tài khoản)
 * @returns {object} bản đã gộp, không sửa a hay b
 */
export function gopWorkspace(a, b) {
  const x = chuan(a);
  const y = chuan(b);
  const xMoiHon = luc(x.capNhat) >= luc(y.capNhat);
  const moi = xMoiHon ? x : y;
  const cu = xMoiHon ? y : x;

  // Hợp hai bảng dấu xoá, mỗi khoá lấy mốc muộn nhất.
  const xoa = { ...y.xoa };
  for (const [k, v] of Object.entries(x.xoa)) {
    if (luc(v) > luc(xoa[k])) xoa[k] = v;
  }
  /** Mục này có bị xoá sau lần sửa cuối không. */
  const daXoa = (khoaMuc, sua) => luc(xoa[khoaMuc]) > 0 && luc(xoa[khoaMuc]) >= luc(sua);

  // Bản nháp: so từng công cụ một.
  const drafts = {};
  for (const id of new Set([...Object.keys(x.drafts), ...Object.keys(y.drafts)])) {
    const dx = x.drafts[id];
    const dy = y.drafts[id];
    if (!dx) drafts[id] = dy;
    else if (!dy) drafts[id] = dx;
    else drafts[id] = luc(dx.updated) >= luc(dy.updated) ? dx : dy;
    if (daXoa(`draft:${id}`, drafts[id]?.updated)) delete drafts[id];
  }

  // Kịch bản: gộp theo id, trùng thì giữ bản sửa sau. Giữ trần 20 như khi lưu
  // trên thiết bị, bỏ bản cũ nhất trước.
  const theoId = new Map();
  for (const s of [...x.scenarios, ...y.scenarios]) {
    if (!s || typeof s.id !== 'string') continue;
    const cu2 = theoId.get(s.id);
    if (!cu2 || luc(s.updated) > luc(cu2.updated)) theoId.set(s.id, s);
  }
  const scenarios = [...theoId.values()]
    .filter((s) => !daXoa(`scenario:${s.id}`, s.updated))
    .sort((p, q) => luc(q.updated) - luc(p.updated))
    .slice(0, 20);

  // Nguồn vốn đã lưu (danh sách quỹ mục tiêu): hợp hai danh sách, không trùng id.
  // Trùng id thì giữ bản có `updated` muộn hơn, vì mỗi mục giờ mang đợt, trạng
  // thái tiếp cận và ghi chú: sửa trạng thái trên điện thoại không được bị bản
  // cũ trên máy tính đè lại. Mục cũ không có `updated` thì bản đang mới hơn thắng.
  // Bỏ lưu ở một bên mà bên kia còn thì vẫn còn, trừ khi có dấu xoá.
  const thuTu = [];
  const muc = new Map();
  for (const s of [...moi.saved, ...cu.saved]) {
    if (!s || typeof s.id !== 'string') continue;
    const daCoMuc = muc.get(s.id);
    if (!daCoMuc) { muc.set(s.id, s); thuTu.push(s.id); continue; }
    if (luc(s.updated) > luc(daCoMuc.updated)) muc.set(s.id, s);
  }
  const saved = thuTu.map((id) => muc.get(id)).filter((s) => !daXoa(`saved:${s.id}`, s.updated));

  // Hồ sơ: so TỪNG Ô theo mốc sửa của chính ô đó.
  //
  // Cách cũ — "ô nào bên mới bỏ trống thì lấy lại bên cũ" — biến thao tác xoá
  // thành thao tác không có tác dụng: người dùng xoá email liên hệ, đồng bộ
  // xong nó quay về. Có mốc từng ô thì ô trống vẫn thắng nếu nó mới hơn.
  const profileMoc = {};
  const profile = {};
  for (const k of new Set([...Object.keys(x.profile), ...Object.keys(y.profile)])) {
    const mx = luc(x.profileMoc[k]) || (k in x.profile ? luc(x.capNhat) : 0);
    const my = luc(y.profileMoc[k]) || (k in y.profile ? luc(y.capNhat) : 0);
    const ben = mx >= my ? x : y;
    const mocK = Math.max(mx, my);
    if (k in ben.profile) profile[k] = ben.profile[k];
    if (mocK) profileMoc[k] = new Date(mocK).toISOString();
  }

  // Dọn dấu xoá đã quá hạn để dữ liệu không phình theo thời gian.
  const han = Date.now() - HAN_DAU_XOA_NGAY * 24 * 60 * 60 * 1000;
  const xoaGon = {};
  for (const [k, v] of Object.entries(xoa)) {
    if (luc(v) >= han) xoaGon[k] = v;
  }

  return {
    version: 1,
    drafts,
    saved,
    tasks: { ...cu.tasks, ...moi.tasks },
    profile,
    profileMoc,
    scenarios,
    xoa: xoaGon,
    capNhat: new Date(Math.max(luc(x.capNhat), luc(y.capNhat)) || Date.now()).toISOString(),
  };
}
